/**
 * MontaTech v2 - Nesting Calculator Service
 *
 * Serviço para calcular nesting via PlotCalc
 * ATUALIZADO: Usa WebSocket para progresso em tempo real
 * FALLBACK: POST HTTP se WebSocket não disponível
 */

import { api, APIError } from "../../../../lib/api";
import { sendToTerminal } from "../../../../lib/terminalLogger";
import {
	socketService,
	PlotCalcResultEvent,
	PlotCalcProgressEvent,
	PlotCalcErrorEvent,
} from "../../../../lib/socketService";
import type { Socket } from "../../../../lib/socketService";
import type { MontaTechCurveData, MontaTechProfile } from "../types";
import { micronsToMm } from "../constants";
import { handleToolsApiError } from "../../../../lib/toolsErrorHandler";
import { getTenantIdForWebSocket } from "../../../../utils/getTenantId";
import { gzipSync } from "fflate";
import { createStallRecoveryWatchdog } from "./stallRecoveryWatchdog";

// ==================== CONSTANTS ====================

/**
 * Timeout para cálculo de nesting via POST (fallback)
 */
const POST_TIMEOUT_MS = 180_000; // 3 minutos

/**
 * Timeout para cálculo de nesting via WebSocket
 */
const WS_TIMEOUT_MS = 300_000; // 5 minutos (backstop final)

/**
 * Janela do watchdog de stall: sem nenhum evento do PlotCalc por este tempo,
 * suspeitamos de conexão zumbi e iniciamos a recuperação.
 *
 * KNOB: o próprio heartbeat do socket.io (~45s, server-side) já detecta a maioria
 * dos zumbis e dispara o caminho disconnect→reconnect→resync já fiado abaixo; este
 * watchdog de aplicação é o BACKSTOP para quando esse caminho não recupera sozinho.
 * 60s equilibra detecção rápida vs. falso-positivo numa fase final legitimamente
 * silenciosa do cálculo. Subir reduz falso-positivo; descer recupera mais cedo.
 */
const WS_STALL_TIMEOUT_MS = 60_000;
/** Uma recuperação; na parada CONSECUTIVA seguinte (sem progresso real), erro fatal. */
const WS_STALL_MAX_RECOVERY_ATTEMPTS = 1;
const WS_RESYNC_EVENT = "plotcalc:resync";

// ==================== TYPES ====================

export interface RFigure {
	qFigure: number; // Índice da curva original (1-based)
	x: number; // Posição X em mm
	y: number; // Posição Y em mm
	rotation: number; // Rotação em graus
}

// Estrutura hierárquica do PlotCalc (materials → lines → sheets → figures)
export interface RFigure {
	materialIndex?: number;
	lineIndex?: number;
	sheetIndex?: number;
	localX?: number;
	localY?: number;
	sheetOffsetX?: number;
	sheetOffsetY?: number;
}

export interface RSheet {
	width_mm: number;
	figures: RFigure[];
}

export interface RLine {
	height_mm: number;
	sheets: RSheet[];
}

export interface MaterialResult {
	materialIndex: number;
	lines: RLine[];
}

export interface CalculateResult {
	success: boolean;
	requestId?: string;
	sessionId?: number;
	maximumY?: number;
	figures?: RFigure[]; // Array flat (compatibilidade)
	materials?: MaterialResult[]; // ✅ Estrutura hierárquica do backend
	/** Flag do buffer PlotCalc — algumas peças podem não ter cabido. */
	errors?: boolean;
	error?: string;
	meta?: {
		backendEmittedAtMs?: number;
		duration?: number;
		frontendResolveAtPerf?: number;
		frontendResultReceivedAtMs?: number;
		frontendResultReceivedAtPerf?: number;
		method: "websocket" | "post";
		unplacedCount?: number;
		unplacedCurveIndices?: number[];
	};
}

export interface CalculateOptions {
	curveData: MontaTechCurveData;
	profile: MontaTechProfile;
	requestId?: string; // ID para tracking/cancelamento
	onProgress?: (percent: number) => void;
	/** Watchdog suspeita de conexão zumbi e iniciou a recuperação → ligar banner "reconectando". */
	onRecovering?: (info: { attempt: number }) => void;
	/** Tráfego real do cálculo voltou (ou recuperação terminou) → desligar banner. */
	onRecovered?: () => void;
}

// ==================== HELPERS ====================

/**
 * Converte o perfil para o formato da API PlotCalc
 */
function buildPlaceConfig(profile: MontaTechProfile) {
	return {
		materialWidth: micronsToMm(profile.materialWith),
		materialHeight: micronsToMm(profile.materialHeight),
		spacing: micronsToMm(profile.otstup),
		margins: {
			left: micronsToMm(profile.leftField),
			right: micronsToMm(profile.rightField),
			top: micronsToMm(profile.upField),
			bottom: micronsToMm(profile.downField),
		},
		rotations: profile.gradusCount,
		nestInside: profile.vnutri_Figuri,
	};
}

/**
 * Converte curvas para o formato da API
 */
function buildCurvesPayload(curveData: MontaTechCurveData) {
	return curveData.curves.map((curve) => ({
		index: curve.index,
		width: curve.width,
		height: curve.height,
		maxLength: curve.maxLength,
		maxLengthScaled: curve.maxLengthScaled,
		paths: curve.paths.map((path) => ({
			segments: path.segments,
		})),
	}));
}

function getSortedKeys(curveData: MontaTechCurveData): number[] {
	return curveData.sortedKeys?.length
		? curveData.sortedKeys
		: curveData.curves.map((curve) => curve.index);
}

function getDuplicateMap(curveData: MontaTechCurveData): Record<string, number> {
	return curveData.duplicateMap || {};
}

function logPayloadSummary(
	method: "websocket" | "post",
	requestId: string,
	curveData: MontaTechCurveData,
	config: ReturnType<typeof buildPlaceConfig>,
	sortedKeys: number[],
	duplicateMap: Record<string, number>,
) {
	sendToTerminal("[PLOTCALC] Payload.Resumo", {
		method,
		requestId,
		firstKey: sortedKeys[0] ?? null,
		spacing: config.spacing,
		curves: curveData.curves.length,
		sortedKeys0to10: sortedKeys.slice(0, 11),
		duplicateCount: Object.keys(duplicateMap).length,
		duplicateSample: Object.entries(duplicateMap)
			.slice(0, 10)
			.map(([duplicateKey, sourceKey]) => ({
				duplicateKey: Number(duplicateKey),
				sourceKey,
			})),
		materialWidth: config.materialWidth,
		materialHeight: config.materialHeight,
		rotations: config.rotations,
		nestInside: config.nestInside,
	});
}

/**
 * Converte resposta do WebSocket (nomes _mm, _degrees) para RFigure[] (nomes internos)
 * WebSocket retorna: curveIndex, x_mm, y_mm, rotation_degrees
 * Sistema interno usa: qFigure, x, y, rotation
 */
function convertWsResultToFigures(
	figures: PlotCalcResultEvent["figures"],
): RFigure[] {
	if (!figures) return [];
	return figures.map((fig) => ({
		materialIndex: fig.materialIndex,
		lineIndex: fig.lineIndex,
		sheetIndex: fig.sheetIndex,
		localX: fig.localX_mm,
		localY: fig.localY_mm,
		sheetOffsetX: fig.sheetOffsetX_mm,
		sheetOffsetY: fig.sheetOffsetY_mm,
		qFigure: fig.curveIndex, // curveIndex → qFigure
		x: fig.x_mm, // x_mm → x (já em mm)
		y: fig.y_mm, // y_mm → y (já em mm)
		rotation: fig.rotation_degrees, // rotation_degrees → rotation (já em graus)
	}));
}

/** Payload C# para nesting:apply-native — preserva offsets do PlotCalc (mesma semântica do preview). */
export function mapFigureToCsharpApplyPayload(f: RFigure) {
	const materialIndex = f.materialIndex ?? 0;
	return {
		curveIndex: f.qFigure,
		x_mm: f.x,
		y_mm: f.y,
		rotation_degrees: f.rotation,
		materialIndex,
		localX_mm: f.localX ?? f.x,
		localY_mm: f.localY ?? f.y,
		sheetOffsetX_mm: f.sheetOffsetX ?? 0,
		sheetOffsetY_mm: f.sheetOffsetY ?? 0,
		...(f.lineIndex != null ? { lineIndex: f.lineIndex } : {}),
		...(f.sheetIndex != null ? { sheetIndex: f.sheetIndex } : {}),
	};
}

/**
 * Monta payload de apply a partir de materials[] — mesma fonte do preview (useCanvas).
 * O array flat figures[] pode trazer materialIndex=0 mesmo em y > altura do material.
 */
export function flattenMaterialsForApplyPayload(materials: MaterialResult[]) {
	const figures: ReturnType<typeof mapFigureToCsharpApplyPayload>[] = [];

	for (const material of materials) {
		const materialIndex = material.materialIndex ?? 0;
		material.lines.forEach((line, lineIdx) => {
			line.sheets.forEach((sheet, sheetIdx) => {
				sheet.figures.forEach((figure) => {
					figures.push(
						mapFigureToCsharpApplyPayload({
							...figure,
							materialIndex,
							lineIndex: figure.lineIndex ?? lineIdx,
							sheetIndex: figure.sheetIndex ?? sheetIdx,
						}),
					);
				});
			});
		});
	}

	return figures;
}

type CsharpApplyFigure = ReturnType<typeof mapFigureToCsharpApplyPayload>;

function isCsharpApplyFigure(fig: RFigure | CsharpApplyFigure): fig is CsharpApplyFigure {
  return typeof (fig as CsharpApplyFigure).curveIndex === 'number';
}

/** Preferir materials[] (hierárquico) quando disponível; fallback para figures[] flat. */
export function buildCsharpApplyFiguresFromResult(result: {
	figures?: Array<RFigure | CsharpApplyFigure>;
	materials?: MaterialResult[] | null;
}): CsharpApplyFigure[] {
	if (result.materials && result.materials.length > 0) {
		return flattenMaterialsForApplyPayload(result.materials);
	}
	const figures = result.figures ?? [];
	if (figures.length > 0 && isCsharpApplyFigure(figures[0])) {
		return figures as CsharpApplyFigure[];
	}
	return figures.map((fig) => mapFigureToCsharpApplyPayload(fig as RFigure));
}

/**
 * Converte materials[] do WebSocket para estrutura interna MaterialResult[]
 * Conforme PLOTCALC_PROTOCOL.md: materials → lines → sheets → figures
 */
function convertWsResultToMaterials(
	materials: PlotCalcResultEvent["materials"],
): MaterialResult[] {
	if (!materials) return [];

	return materials.map((material) => ({
		materialIndex: material.materialIndex,
		lines: material.lines.map((line) => ({
			height_mm: line.height_mm,
			sheets: line.sheets.map((sheet) => ({
				width_mm: sheet.width_mm,
				figures: sheet.figures.map((fig) => ({
					materialIndex: fig.materialIndex ?? material.materialIndex,
					lineIndex: fig.lineIndex,
					sheetIndex: fig.sheetIndex,
					localX: fig.localX_mm,
					localY: fig.localY_mm,
					sheetOffsetX: fig.sheetOffsetX_mm,
					sheetOffsetY: fig.sheetOffsetY_mm,
					qFigure: fig.curveIndex,
					x: fig.x_mm,
					y: fig.y_mm,
					rotation: fig.rotation_degrees,
				})),
			})),
		})),
	}));
}

function normalizePlotCalcResponseData(
	data: any,
): Pick<CalculateResult, "maximumY" | "figures" | "materials" | "errors"> {
	if (!data) {
		return {
			maximumY: undefined,
			figures: [],
			materials: [],
			errors: false,
		};
	}

	return {
		maximumY: data.maximumY_mm ?? data.maximumY,
		figures: convertWsResultToFigures(data.figures),
		materials: convertWsResultToMaterials(data.materials),
		errors: data.errors === true,
	};
}

// ==================== WEBSOCKET NESTING ====================

/**
 * Calcula nesting via WebSocket (preferido)
 */
async function calculateViaWebSocket(
	options: CalculateOptions,
	externalRequestId?: string,
): Promise<CalculateResult> {
	const { curveData, profile, onProgress, onRecovering, onRecovered } = options;
	const requestId = externalRequestId || `montatech-ws-${Date.now()}`;

	return new Promise((resolve) => {
		let timeoutId: NodeJS.Timeout | null = null;
		let resolved = false;
		let bannerShown = false; // banner "reconectando" visível na UI
		let tEmitSent = 0; // será setado quando o emit for enviado
		let sharedSocket: Socket | null = null;
		let lastSocketActivityAt = Date.now();
		let lastProgressAt: number | null = null;
		let shouldResyncOnReconnect = false;
		let sawDisconnectDuringCalculation = false;
		let handleSocketDisconnect: ((reason: string) => void) | null = null;
		let handleSocketConnect: (() => void) | null = null;

		const stallWatchdog = createStallRecoveryWatchdog({
			timeoutMs: WS_STALL_TIMEOUT_MS,
			maxRecoveryAttempts: WS_STALL_MAX_RECOVERY_ATTEMPTS,
			onRecoverableStall: (attempt) => {
				if (resolved) return;

				const silentForMs = Date.now() - lastSocketActivityAt;
				const transportName =
					sharedSocket?.io?.engine?.transport?.name || "unknown";

				sendToTerminal(
					"⚠️ [CALC/WS] Sem eventos do PlotCalc; tentando recuperar a conexão",
					{
						requestId,
						attempt,
						silentForMs,
						lastProgressAt,
						socketId: sharedSocket?.id || null,
						connected: sharedSocket?.connected ?? false,
						transport: transportName,
					},
				);

				// Ligar banner "reconectando" na UI (mantém status 'calculating')
				if (!bannerShown) {
					bannerShown = true;
					onRecovering?.({ attempt });
				}

				shouldResyncOnReconnect = true;
				// rearm mantém o orçamento de tentativas → próxima parada sem
				// progresso real escala para fatal. O re-emit do execute acontece
				// no handleSocketConnect, assim que a reconexão completa.
				stallWatchdog.rearm();
				socketService.refreshAuthAndReconnect("watchdog:stalled", {
					source: "montatech-watchdog",
					reason: `stall-recover:${requestId}:attempt-${attempt}`,
				});
			},
			onFatalStall: (attempt) => {
				if (resolved) return;
				resolved = true;

				// Desliga o banner antes do toast de erro assumir
				if (bannerShown) {
					bannerShown = false;
					onRecovered?.();
				}

				sendToTerminal(
					"❌ [CALC/WS] Recuperação esgotada após silêncio prolongado do cálculo",
					{
						requestId,
						attempt,
						lastProgressAt,
						lastSocketActivityAt,
					},
				);

				cleanup();
				resolve({
					success: false,
					requestId,
					error:
						"[STALL] O cálculo perdeu sincronização durante a etapa final. Tente novamente.",
					meta: { method: "websocket" },
				});
			},
		});

		const noteSocketActivity = (event: string, extra?: Record<string, unknown>) => {
			lastSocketActivityAt = Date.now();
			// Evento REAL do PlotCalc → zera o orçamento de tentativas (ciclo de
			// paradas consecutivas recomeça) e reagenda a janela.
			stallWatchdog.noteActivity();

			if (event === "progress") {
				lastProgressAt = lastSocketActivityAt;
			}

			// Tráfego real voltou → desliga o banner "reconectando"
			if (bannerShown) {
				bannerShown = false;
				onRecovered?.();
			}

			if (import.meta.env.DEV) {
				sendToTerminal("🫀 [CALC/WS] Atividade observada no cálculo", {
					requestId,
					event,
					socketId: sharedSocket?.id || null,
					...extra,
				});
			}
		};

		const emitResync = (reason: string) => {
			sendToTerminal("🔁 [CALC/WS] Solicitando re-sync do cálculo em andamento", {
				requestId,
				reason,
				socketId: sharedSocket?.id || null,
				recovered: sharedSocket?.recovered ?? false,
			});

			socketService.emit(
				WS_RESYNC_EVENT,
				{
					requestId,
					tenantId: getTenantIdForWebSocket(),
				},
				{
					source: "montatech-watchdog",
					reason: `emit-plotcalc-resync:${requestId}:${reason}`,
				},
			);
		};

		// Payload comprimido (montado uma única vez) — reusado no emit inicial e
		// nas re-emissões de recuperação, evitando um segundo gzip.
		const sortedKeys = getSortedKeys(curveData);
		const duplicateMap = getDuplicateMap(curveData);
		const config = buildPlaceConfig(profile);

		logPayloadSummary("websocket", requestId, curveData, config, sortedKeys, duplicateMap);

		const payload = {
			requestId,
			tenantId: getTenantIdForWebSocket(),
			curves: buildCurvesPayload(curveData),
			// CRITICAL: sortedKeys follow curve IDs and legacy order.
			sortedKeys,
			duplicateMap,
			config,
		};

		const tGzip = performance.now();
		const jsonBytes = new TextEncoder().encode(JSON.stringify(payload));
		const compressed = gzipSync(jsonBytes);
		const tGzipEnd = performance.now();
		sendToTerminal(
			`📤 [CALC/WS] GZip: ${Math.round(jsonBytes.length / 1024)}KB → ${Math.round(compressed.length / 1024)}KB (${Math.round((1 - compressed.length / jsonBytes.length) * 100)}% redução) em ${Math.round(tGzipEnd - tGzip)}ms`,
		);
		const executePayload = { __gzip: true as const, data: compressed };

		// Emite o cálculo. reason "initial" no primeiro envio; "reconnect" na
		// recuperação (cai no cache curvas+config do backend e devolve o resultado).
		const emitExecute = (reason: string) => {
			tEmitSent = performance.now();
			socketService.emit("plotcalc:execute", executePayload, {
				source: "montatech-execute",
				reason: `emit-plotcalc-execute:${requestId}:${reason}`,
			});
		};

		// Cleanup de listeners
		const cleanup = () => {
			if (timeoutId) clearTimeout(timeoutId);
			stallWatchdog.clear();
			socketService.off("plotcalc:progress", handleProgress);
			socketService.off("plotcalc:result", handleResult);
			socketService.off("plotcalc:error", handleError);
			socketService.off("plotcalc:noMoreCombinations", handleNoMore);
			if (sharedSocket && handleSocketDisconnect) {
				sharedSocket.off("disconnect", handleSocketDisconnect);
			}
			if (sharedSocket && handleSocketConnect) {
				sharedSocket.off("connect", handleSocketConnect);
			}
		};

		// Handler de progresso
		const handleProgress = (data: PlotCalcProgressEvent) => {
			if (data.requestId !== requestId) return;
			const percent = Math.min(data.percent, 100);
			noteSocketActivity("progress", { percent });
			onProgress?.(percent);
		};

		// Handler de resultado
		const handleResult = (data: PlotCalcResultEvent) => {
			if (data.requestId !== requestId) return;
			if (resolved) return;
			resolved = true;
			const resultReceivedAtPerf = performance.now();
			const resultReceivedAtMs = Date.now();
			noteSocketActivity("result", {
				figures: data.figures?.length ?? 0,
				materials: data.materials?.length ?? 0,
			});

			const tResult = resultReceivedAtPerf;
			sendToTerminal(
				`✅ [CALC/WS] Resultado recebido — ${data.figures?.length ?? 0} figuras, ${data.materials?.length ?? 0} materials (${Math.round(tResult - tEmitSent)}ms desde emit)`,
			);
			sendToTerminal("📥 [CALC/WS] Evento plotcalc:result recebido no frontend", {
				requestId,
				receivedAtMs: resultReceivedAtMs,
				backendEmittedAtMs: data.meta?.emittedAtMs ?? null,
				backendToFrontendMs:
					typeof data.meta?.emittedAtMs === "number"
						? resultReceivedAtMs - data.meta.emittedAtMs
						: null,
			});
			if (data.connection) {
				sendToTerminal("🛰️ [CALC/WS] Rota do backend confirmada", {
					route: data.connection.route,
					useProxy: data.connection.useProxy,
					connectHost: data.connection.connectHost,
					connectPort: data.connection.connectPort,
					proxyHost: data.connection.proxyHost,
					proxyPort: data.connection.proxyPort,
					fallbackReason: data.connection.fallbackReason,
					proxyHealthStatus: data.connection.proxyHealthStatus,
					proxyHealthCheckedAt: data.connection.proxyHealthCheckedAt,
				});
			}

			// Medir tamanho raw do payload recebido
			try {
				const rawSize = JSON.stringify(data).length;
				sendToTerminal(
					`📊 [CALC/WS] Payload recebido: ${Math.round(rawSize / 1024)}KB raw JSON`,
				);
			} catch {
				/* ignore */
			}

			cleanup();
			sendToTerminal("🧹 [CALC/WS] Cleanup de listeners concluído", {
				requestId,
				elapsedMs: Math.round(performance.now() - tResult),
			});

			const figuresCount = data.figures?.length ?? 0;
			const materialsCount = data.materials?.length ?? 0;
			const hasResultPayload = figuresCount > 0 || materialsCount > 0;

			// Aceita resultado parcial: backend marca success=false quando alguma
			// curva da request não coube em nenhum material, mas as demais figuras
			// posicionadas continuam válidas. Só descartamos se vier vazio mesmo.
			if (hasResultPayload) {
				const t1 = performance.now();
				const figures = convertWsResultToFigures(data.figures);
				const t2 = performance.now();
				const materials = convertWsResultToMaterials(data.materials);
				const t3 = performance.now();
				sendToTerminal(
					`⏱️ [CALC/WS] Conversão: figures=${Math.round(t2 - t1)}ms, materials=${Math.round(t3 - t2)}ms`,
				);

				// Set diff: identifica curves enviadas que não voltaram posicionadas
				const placedSet = new Set(
					(data.figures ?? []).map((f) => f.curveIndex),
				);
				const unplacedCurveIndices = curveData.curves
					.map((c) => c.index)
					.filter((idx) => !placedSet.has(idx));
				const unplacedCount = unplacedCurveIndices.length;

				if (unplacedCount > 0) {
					sendToTerminal(
						`⚠️ [CALC/WS] Nesting parcial: ${figuresCount} de ${curveData.curves.length} curvas posicionadas, ${unplacedCount} não couberam`,
						{
							unplacedCurveIndices: unplacedCurveIndices.slice(0, 50),
							backendSuccessFlag: data.success,
						},
					);
				}

				sendToTerminal(
					`⏱️ [CALC/WS] Chamando resolve()... (total handleResult: ${Math.round(performance.now() - tResult)}ms)`,
				);
				const frontendResolveAtPerf = performance.now();
				sendToTerminal("📤 [CALC/WS] Resolve entregue ao useMontaTechState", {
					requestId,
					handleResultMs: Math.round(frontendResolveAtPerf - tResult),
					frontendResultReceivedAtPerf: Math.round(resultReceivedAtPerf),
					frontendResolveAtPerf: Math.round(frontendResolveAtPerf),
				});
				resolve({
					success: true,
					requestId,
					sessionId: data.sessionId,
					maximumY: data.maximumY_mm,
					figures,
					materials,
					errors: data.errors === true,
					meta: {
						backendEmittedAtMs: data.meta?.emittedAtMs,
						duration: data.meta?.duration,
						frontendResolveAtPerf,
						frontendResultReceivedAtMs: resultReceivedAtMs,
						frontendResultReceivedAtPerf: resultReceivedAtPerf,
						method: "websocket",
						unplacedCount,
						unplacedCurveIndices,
					},
				});
			} else {
				resolve({
					success: false,
					requestId,
					error: "Cálculo não retornou figuras",
					meta: { method: "websocket" },
				});
			}
		};

		// Handler de erro
		const handleError = (data: PlotCalcErrorEvent) => {
			if (data.requestId !== requestId) return;
			if (resolved) return;
			resolved = true;
			noteSocketActivity("error", {
				code: data.code,
			});

			sendToTerminal("❌ [CALC/WS] Erro", {
				code: data.code,
				message: data.message,
				connection: data.connection,
			});

			cleanup();
			resolve({
				success: false,
				requestId,
				error: `[${data.code}] ${data.message}`,
				meta: { method: "websocket" },
			});
		};

		// Handler de sem mais combinações
		const handleNoMore = (data: { requestId: string }) => {
			if (data.requestId !== requestId) return;
			noteSocketActivity("noMoreCombinations");
		};

		// Timeout
		timeoutId = setTimeout(() => {
			if (resolved) return;
			resolved = true;

			sendToTerminal("⏰ [CALC/WS] Timeout após", { ms: WS_TIMEOUT_MS });

			cleanup();
			resolve({
				success: false,
				requestId,
				error: "[TIMEOUT] Cálculo demorou demais. Tente com menos objetos.",
				meta: { method: "websocket" },
			});
		}, WS_TIMEOUT_MS);

		// Registrar listeners
		sharedSocket = socketService.getSocket({
			source: "montatech-execute",
			reason: `prepare-shared-socket:${requestId}`,
		});
		if (!sharedSocket) {
			sendToTerminal("❌ [CALC/WS] Socket compartilhado indisponível", {
				requestId,
			});
			cleanup();
			resolve({
				success: false,
				requestId,
				error: "[SOCKET] Não foi possível iniciar o socket compartilhado.",
				meta: { method: "websocket" },
			});
			return;
		}
		handleSocketDisconnect = (reason: string) => {
			if (resolved) return;

			sawDisconnectDuringCalculation = true;
			sendToTerminal("⚠️ [CALC/WS] Socket desconectado durante o cálculo", {
				requestId,
				reason,
				socketId: sharedSocket?.id || null,
			});
		};
		handleSocketConnect = () => {
			if (resolved) return;

			const recovered = sharedSocket?.recovered ?? false;
			const transportName =
				sharedSocket?.io?.engine?.transport?.name || "unknown";
			const shouldResync = shouldResyncOnReconnect || sawDisconnectDuringCalculation;

			sendToTerminal("🔄 [CALC/WS] Socket conectado durante o cálculo", {
				requestId,
				recovered,
				socketId: sharedSocket?.id || null,
				transport: transportName,
				shouldResync,
			});

			lastSocketActivityAt = Date.now();
			// Reconexão de transporte NÃO é progresso de cálculo: reagenda mantendo
			// o orçamento (rearm), para que uma reconexão sem resultado ainda escale
			// para fatal. Só progress/result real (noteActivity) zera o orçamento.
			stallWatchdog.rearm();

			if (shouldResync && !recovered) {
				// connectionStateRecovery NÃO restaurou a sessão (recovered=false):
				// pedimos o resync (no-op no backend atual) e re-emitimos o execute,
				// que cai no cache curvas+config do backend e devolve o resultado.
				emitResync("reconnect");
				emitExecute("reconnect");
			}
			// Se recovered=true, o backend restaurou a sessão e o plotcalc:result
			// bufferizado volta sozinho — não re-emitir.

			shouldResyncOnReconnect = false;
			sawDisconnectDuringCalculation = false;
		};
		sharedSocket.on("disconnect", handleSocketDisconnect);
		sharedSocket.on("connect", handleSocketConnect);
		if (import.meta.env.DEV) {
			sendToTerminal("🧭 [CALC/WS] Usando socket compartilhado da aplicação", {
				requestId,
				source: "montatech-execute",
			});
		}
		socketService.on("plotcalc:progress", handleProgress, {
			source: "montatech-execute",
			reason: `listen-progress:${requestId}`,
		});
		socketService.on("plotcalc:result", handleResult, {
			source: "montatech-execute",
			reason: `listen-result:${requestId}`,
		});
		socketService.on("plotcalc:error", handleError, {
			source: "montatech-execute",
			reason: `listen-error:${requestId}`,
		});
		socketService.on("plotcalc:noMoreCombinations", handleNoMore, {
			source: "montatech-execute",
			reason: `listen-no-more:${requestId}`,
		});

		// Emitir evento com payload comprimido (montado acima)
		emitExecute("initial");
		sendToTerminal(
			`📤 [CALC/WS] Emit enviado, aguardando resposta do backend...`,
		);
		stallWatchdog.arm();
	});
}

// ==================== POST FALLBACK ====================

/**
 * Calcula nesting via POST HTTP (fallback)
 */
async function calculateViaPost(
	options: CalculateOptions,
	externalRequestId?: string,
): Promise<CalculateResult> {
	const { curveData, profile, onProgress } = options;
	const requestId = externalRequestId || `montatech-post-${Date.now()}`;

	// Progresso simulado para feedback
	let progressInterval: NodeJS.Timeout | null = null;
	let currentProgress = 0;

	if (onProgress) {
		progressInterval = setInterval(() => {
			if (currentProgress < 90) {
				currentProgress += Math.random() * 5;
				onProgress(Math.min(currentProgress, 90));
			}
		}, 500);
	}

	try {
		const sortedKeys = getSortedKeys(curveData);
		const duplicateMap = getDuplicateMap(curveData);
		const config = buildPlaceConfig(profile);

		logPayloadSummary("post", requestId, curveData, config, sortedKeys, duplicateMap);

		const payload = {
			command: 5, // PLACE
			requestId,
			curves: buildCurvesPayload(curveData),
			// CRITICAL: sortedKeys follow curve IDs and legacy order.
			sortedKeys,
			duplicateMap,
			config,
		};

		sendToTerminal("📤 [CALC/POST] Enviando via HTTP", {
			requestId,
			curves: curveData.count,
			profile: profile.name,
			timeoutMs: POST_TIMEOUT_MS,
		});

		// ✅ PADRONIZADO: Usar api() helper com timeout customizado
		// api() já trata refresh automático de accessToken e headers corretos
		const controller = new AbortController();
		const timeoutId = setTimeout(() => controller.abort(), POST_TIMEOUT_MS);

		let response: any;
		try {
			// Usar api() helper que já trata token e refresh automaticamente
			response = await Promise.race([
				api("/plotcalc/execute", {
					method: "POST",
					body: JSON.stringify(payload),
					signal: controller.signal,
				}),
				new Promise((_, reject) =>
					setTimeout(
						() => reject(new Error("Request timeout")),
						POST_TIMEOUT_MS,
					),
				),
			]);
		} catch (raceError: any) {
			clearTimeout(timeoutId);
			// Se foi timeout do race, lançar erro apropriado
			if (
				raceError?.message?.includes("timeout") ||
				raceError?.name === "AbortError"
			) {
				throw new APIError(0, "Requisição expirou (timeout)", "timeout", {});
			}
			throw raceError;
		}

		clearTimeout(timeoutId);

		if (progressInterval) {
			clearInterval(progressInterval);
			onProgress?.(100);
		}

		if (response?.success && response?.data) {
			const normalized = normalizePlotCalcResponseData(response.data);
			return {
				success: true,
				requestId,
				sessionId: response.data.sessionId,
				maximumY: normalized.maximumY,
				figures: normalized.figures,
				materials: normalized.materials,
				errors: normalized.errors,
				meta: { method: "post" },
			};
		} else {
			const errorCode = response?.error?.code || "API_ERROR";
			const errorMsg =
				response?.error?.message || response?.message || "Erro desconhecido";
			return {
				success: false,
				requestId,
				error: `[${errorCode}] ${errorMsg}`,
				meta: { method: "post" },
			};
		}
	} catch (err: unknown) {
		if (progressInterval) clearInterval(progressInterval);

		let errorMsg = "Erro de conexão";
		let errorCode = "UNKNOWN";

		// ✅ MIGRADO: Usar handleToolsApiError para melhor tratamento
		if (err instanceof APIError) {
			// Criar novo APIError com metadata enriquecido (evitar mutação)
			const enhancedError = new APIError(err.status, err.message, err.kind, {
				...err.data,
				url: "/plotcalc/execute",
			});

			// Converter para AppError usando handler centralizado
			const appError = handleToolsApiError(enhancedError, "plotcalc");
			errorCode = appError.code;
			errorMsg = appError.message;
		} else if (err instanceof Error) {
			if (err.message.includes("aborted")) {
				errorCode = "TIMEOUT";
				errorMsg = "Requisição expirou";
			} else {
				errorMsg = err.message;
			}
		}

		sendToTerminal("❌ [CALC/POST] Erro", { errorCode, errorMsg });

		return {
			success: false,
			requestId,
			error: `[${errorCode}] ${errorMsg}`,
			meta: { method: "post" },
		};
	}
}

// ==================== MAIN FUNCTION ====================

/**
 * Timeout para aguardar conexão WebSocket (10 segundos)
 */
const WS_CONNECT_TIMEOUT_MS = 10_000;

/**
 * Calcula nesting via PlotCalc
 * APENAS WebSocket - sem fallback POST
 */
export async function calculateNesting(
	options: CalculateOptions,
): Promise<CalculateResult> {
	const { curveData, profile, requestId: externalRequestId } = options;
	const requestId = externalRequestId || `montatech-${Date.now()}`;

	sendToTerminal(
		`🔄 [MONTATECH/CALC] Iniciando cálculo — ${curveData.count} curvas, perfil="${profile.name}"`,
	);

	// ✅ ensureConnected encapsula: já conectado → ok / nulo → connect() / desconectado → reanima.
	// Aguarda evento `connect` em vez de fazer polling 100ms.
	// Tenta 1 retry rápido se a primeira tentativa falhar por timeout (ex.: backoff acabou de
	// recomeçar). Auth_failed não retenta — caller mostra erro claro ao usuário.
	const attemptEnsure = async () => socketService.ensureConnected(WS_CONNECT_TIMEOUT_MS);

	let result = await attemptEnsure();
	if (!result.ok && result.reason === "timeout") {
		sendToTerminal("🔄 [CALC] Primeiro ensureConnected falhou por timeout — retry");
		result = await attemptEnsure();
	}

	if (result.ok) {
		return calculateViaWebSocket(options, requestId);
	}

	// Falha definitiva — mensagem específica por motivo
	sendToTerminal("❌ [CALC] WebSocket não conectou", {
		reason: result.reason,
		message: result.message,
	});

	const errorByReason: Record<typeof result.reason, string> = {
		timeout:
			"[WS_CONNECTION_FAILED] WebSocket não conectou no tempo limite. Verifique sua conexão e tente novamente.",
		auth_failed:
			"[WS_AUTH_FAILED] Sessão expirada ou inválida. Faça login novamente.",
		no_socket:
			"[WS_NO_SOCKET] Não foi possível instanciar o WebSocket. Tente recarregar a página.",
		error: `[WS_ERROR] ${result.message ?? "Erro desconhecido"}`,
	};

	return {
		success: false,
		requestId,
		error: errorByReason[result.reason],
		meta: { method: "websocket" },
	};
}

// ==================== OTHER FUNCTIONS ====================

/**
 * Solicita mais combinações
 */
export async function moreCombinations(
	sessionRequestId: string,
): Promise<CalculateResult> {
	try {
		const response = await api("/plotcalc/execute", {
			method: "POST",
			body: JSON.stringify({
				command: 7,
				sessionRequestId,
			}),
			headers: { "Content-Type": "application/json" },
		});

		if (response.success && response.data) {
			const normalized = normalizePlotCalcResponseData(response.data);
			return {
				success: true,
				requestId: sessionRequestId,
				sessionId: response.data.sessionId,
				maximumY: normalized.maximumY,
				figures: normalized.figures,
				materials: normalized.materials,
			};
		} else {
			return {
				success: false,
				requestId: sessionRequestId,
				error: response.message || "Sem mais combinações",
			};
		}
	} catch (err) {
		return {
			success: false,
			requestId: sessionRequestId,
			error: err instanceof Error ? err.message : "Erro",
		};
	}
}

/**
 * Para o cálculo em andamento
 */
export async function stopCalculation(sessionRequestId: string): Promise<void> {
	try {
		await api("/plotcalc/execute", {
			method: "POST",
			body: JSON.stringify({ command: 6, sessionRequestId }),
			headers: { "Content-Type": "application/json" },
		});
	} catch (err) {}
}

/**
 * Fecha a sessão
 */
export async function closeSession(sessionRequestId: string): Promise<void> {
	try {
		await api("/plotcalc/execute", {
			method: "POST",
			body: JSON.stringify({ command: 777, sessionRequestId }),
			headers: { "Content-Type": "application/json" },
		});
	} catch (err) {}
}

// ==================== OPTIMIZE ====================

export type OptimizeLevel = "light" | "classic" | "premium";

export const OPTIMIZE_ATTEMPTS: Record<OptimizeLevel, number> = {
	light: 20,
	classic: 50,
	premium: 100,
};

export interface OptimizeResult {
	success: boolean;
	requestId?: string;
	maximumY?: number;
	figures?: RFigure[];
	materials?: MaterialResult[];
	errors?: boolean;
	error?: string;
	meta?: {
		duration: number;
		attemptsDone: number;
		maxAttempts: number;
		improvements: number;
		exhausted: boolean;
	};
}

/**
 * Otimiza o resultado do nesting via WebSocket.
 * O backend faz o loop de moreCombinations e emite progress/result via Socket.IO.
 * Suporta cancelamento via cancelOptimize().
 */
export async function optimizeNesting(options: {
	requestId: string;
	level: OptimizeLevel;
	onProgress?: (data: {
		attempt: number;
		maxAttempts: number;
		bestMaximumY: number | null;
		improvements: number;
		percent: number;
		calcPercent?: number;
		passes?: number;
	}) => void;
}): Promise<OptimizeResult> {
	const { requestId, level, onProgress } = options;
	const maxAttempts = OPTIMIZE_ATTEMPTS[level];

	sendToTerminal(
		`[MONTATECH/OPTIMIZE] Iniciando via WebSocket: ${level} (${maxAttempts} tentativas)`,
	);

	return new Promise((resolve) => {
		let resolved = false;
		let timeoutId: NodeJS.Timeout | null = null;

		const cleanup = () => {
			if (timeoutId) clearTimeout(timeoutId);
			socketService.off("optimize:progress", handleProgress);
			socketService.off("optimize:result", handleResult);
			socketService.off("optimize:error", handleError);
		};

		const handleProgress = (data: any) => {
			if (data.requestId !== requestId) return;
			sendToTerminal(
				`📊 [OPTIMIZE/WS] #${data.attempt}/${data.maxAttempts} — ${data.percent}% | melhorias=${data.improvements} | best=${data.bestMaximumY ?? "N/A"} | calc=${data.calcPercent ?? "-"}% | passes=${data.passes ?? 0}`,
			);
			onProgress?.({
				attempt: data.attempt || 0,
				maxAttempts: data.maxAttempts || maxAttempts,
				bestMaximumY: data.bestMaximumY ?? null,
				improvements: data.improvements || 0,
				percent: data.percent || 0,
				calcPercent: data.calcPercent,
				passes: data.passes,
			});
		};

		const handleResult = (data: any) => {
			if (data.requestId !== requestId) return;
			if (resolved) return;
			resolved = true;
			cleanup();

			sendToTerminal(
				`✅ [OPTIMIZE/WS] Resultado: ${data.figures?.length} figuras, ${data.meta?.improvements} melhorias em ${data.meta?.duration}ms`,
			);

			const normalized = normalizePlotCalcResponseData(data);
			resolve({
				success: true,
				requestId,
				maximumY: normalized.maximumY,
				figures: normalized.figures,
				materials: normalized.materials,
				errors: normalized.errors,
				meta: data.meta,
			});
		};

		const handleError = (data: any) => {
			if (data.requestId !== requestId) return;
			if (resolved) return;
			resolved = true;
			cleanup();

			sendToTerminal(`❌ [OPTIMIZE/WS] Erro: ${data.code} - ${data.message}`);
			resolve({
				success: false,
				requestId,
				error: data.message || "Erro na otimização",
			});
		};

		// Timeout: maxAttempts * 300ms (cada tentativa ~200-300ms no PlotCalc)
		const timeoutMs = Math.max(60000, maxAttempts * 300);
		timeoutId = setTimeout(() => {
			if (resolved) return;
			resolved = true;
			cleanup();
			sendToTerminal(`⏰ [OPTIMIZE/WS] Timeout após ${timeoutMs}ms`);
			resolve({
				success: false,
				requestId,
				error: "[TIMEOUT] Otimização demorou demais.",
			});
		}, timeoutMs);

		// Registrar listeners
		socketService.on("optimize:progress", handleProgress);
		socketService.on("optimize:result", handleResult);
		socketService.on("optimize:error", handleError);

		// Emitir comando de optimize
		sendToTerminal(
			`[OPTIMIZE/WS] Emitindo plotcalc:optimize — requestId=${requestId}, maxAttempts=${maxAttempts}`,
		);
		socketService.emit("plotcalc:optimize", { requestId, maxAttempts });
	});
}

/**
 * Cancela uma otimização em andamento
 */
export function cancelOptimize(requestId: string): void {
	sendToTerminal(`[OPTIMIZE/CANCEL] Cancelando otimização: ${requestId}`);
	socketService.emit("plotcalc:cancel-optimize", { requestId });
}
